'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { Text } from '../../../../../components/text';
import type { WebSocketExecutionKind } from './parse-websocket-execution-view';

type WebSocketExecutionShellProps = {
  mode: WebSocketExecutionKind;
  status: ReactNode;
  error?: string;
  testId: string;
  children?: ReactNode;
};

export function WebSocketExecutionShell({
  mode,
  status,
  error,
  testId,
  children,
}: WebSocketExecutionShellProps) {
  const t = useTranslations('designSystem.chat.websocketClient');
  const tChat = useTranslations('designSystem.chat');
  const modeLabel = (() => {
    switch (mode) {
      case 'connect':
        return t('modeConnect');
      case 'send':
        return t('modeSend');
      case 'poll':
        return t('modePoll');
      case 'stop':
        return t('modeStop');
      default: {
        const unhandled: never = mode;

        return unhandled;
      }
    }
  })();

  return (
    <Box
      data-testid={testId}
      borderWidth="1px"
      borderColor="border.neutral.muted"
      borderRadius="md"
      bg="transparent"
      overflow="hidden"
      width="100%"
    >
      <HStack
        justify="space-between"
        alignItems="center"
        px={3}
        h="36px"
        bg="transparent"
        borderBottomWidth={children || error ? '1px' : '0'}
        borderBottomColor="border.neutral.muted"
      >
        <Text fontSize="xs" fontWeight="medium" color="fg.default">
          {t('title')} · {modeLabel}
        </Text>
        {status}
      </HStack>
      {error ? (
        <Box
          px={3}
          py={2}
          borderBottomWidth={children ? '1px' : '0'}
          borderColor="border.neutral.muted"
        >
          <Text fontSize="xs" color="fg.error" whiteSpace="pre-wrap">
            {error}
          </Text>
        </Box>
      ) : null}
      {children ? (
        <VStack alignItems="stretch" gap={2} width="100%" px={3} py={2}>
          {children}
        </VStack>
      ) : null}
      {!children && !error ? (
        <Box px={3} py={2}>
          <Text fontSize="xs" color="fg.muted">
            {tChat('noOutput')}
          </Text>
        </Box>
      ) : null}
    </Box>
  );
}
